'use client';

import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/lib/utils';

const accountDeletionSchema = z
  .object({
    app: z.string().min(1, 'Please select the app or service'),
    appOther: z.string().optional(),
    email: z.string().email('Please enter a valid email address'),
    username: z.string().min(1, 'Please enter your username or account ID'),
    phoneNumber: z.string().optional(),
    reason: z.string().min(1, 'Please select a reason for deletion'),
    additionalInfo: z.string().optional(),
    confirmDeletion: z.boolean().refine((val) => val === true, {
      message: 'You must confirm that you understand this action is irreversible',
    }),
  })
  .refine((data) => data.app !== 'other' || (data.appOther && data.appOther.trim().length > 0), {
    message: 'Please name the app or service',
    path: ['appOther'],
  });

type AccountDeletionFormData = z.infer<typeof accountDeletionSchema>;

const apps = [
  { value: '', label: 'Select an app or service' },
  { value: 'Fitverse', label: 'Fitverse' },
  { value: 'Tech Corner', label: 'Tech Corner' },
  { value: 'SooqSolar', label: 'SooqSolar' },
  { value: 'Facebook / Meta Login data', label: 'Facebook / Meta Login data' },
  { value: 'other', label: 'Other app or service' },
];

const deletionReasons = [
  { value: '', label: 'Select a reason' },
  { value: 'privacy-concerns', label: 'Privacy concerns' },
  { value: 'not-using-app', label: 'No longer using the app' },
  { value: 'found-alternative', label: 'Found an alternative app' },
  { value: 'too-many-notifications', label: 'Too many notifications' },
  { value: 'technical-issues', label: 'Technical issues with the app' },
  { value: 'account-security', label: 'Account security concerns' },
  { value: 'data-storage', label: 'Concerns about data storage' },
  { value: 'other', label: 'Other reason' },
];

// Word the user must type to unlock the destructive action.
const CONFIRMATION_WORD = 'DELETE';

const consequences = [
  'Your profile, login, and account ID',
  'All saved preferences and settings',
  'Your full activity and usage history',
  'Data received through Facebook (Meta) Login, if any',
];

/* Field styling — filled, contained inputs (not bare underlines) so the
   form holds together at any width. */
const fieldBase =
  'w-full rounded-lg border bg-black/25 px-4 py-3.5 text-base text-white outline-none transition-colors duration-200 placeholder:text-white/30 focus-visible:border-white/50 focus-visible:bg-black/35';
const fieldIdle = 'border-white/[0.12] hover:border-white/25';
const fieldError = 'border-red-400/70 bg-red-500/[0.04]';

const labelBase = 'mb-2.5 block text-[11px] font-medium uppercase tracking-[0.2em] text-white/55';

const selectArrow =
  'bg-[url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'rgba(255,255,255,0.5)\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'/%3E%3C/svg%3E")] bg-[right_14px_center] bg-[length:18px] bg-no-repeat pr-12';

/* Numbered step header — Six Caps numerals tie the form to the site's
   display typography. */
function StepHeader({ number, title }: { number: string; title: string }) {
  return (
    <div className="mb-7 flex items-center gap-4">
      <span aria-hidden="true" className="font-six-caps text-4xl leading-none text-white/20">
        {number}
      </span>
      <h3 className="text-xs font-semibold uppercase tracking-[0.25em] text-white/75">{title}</h3>
      <span aria-hidden="true" className="h-px flex-1 bg-white/[0.08]" />
    </div>
  );
}

/**
 * `lockedApp` — when set (e.g. from an app-specific deletion page like
 * /account-deletion-sooqsolar), the "which app" selector is replaced by a
 * read-only row and the app is fixed to this value. This lets every
 * app-specific page reuse this single, battle-tested form (with the
 * type-to-confirm gate) instead of maintaining divergent copies.
 */
export function AccountDeletionForm({ lockedApp }: { lockedApp?: string } = {}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [confirmText, setConfirmText] = useState('');

  const baseId = useId();
  const fid = (name: string) => `${baseId}-${name}`;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<AccountDeletionFormData>({
    resolver: zodResolver(accountDeletionSchema),
    defaultValues: {
      app: lockedApp ?? '',
      confirmDeletion: false,
    },
  });

  const selectedApp = watch('app');
  const selectedReason = watch('reason');
  const isConfirmed = confirmText.trim().toUpperCase() === CONFIRMATION_WORD;

  const onSubmit = async (data: AccountDeletionFormData) => {
    // Belt-and-braces: the button is disabled until the word matches, but
    // never let the destructive request through without it.
    if (!isConfirmed) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const functionUrl = process.env.NEXT_PUBLIC_ACCOUNT_DELETION_FUNCTION_URL || '/api/account-deletion';
      const { app, appOther, ...rest } = data;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...rest,
          app: app === 'other' ? (appOther ?? '').trim() : app,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        reset();
        setConfirmText('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="self-styled overflow-hidden rounded-2xl border border-white/10 bg-[#0d211e]/90 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)]"
    >
      {/* Danger strip — header of the card, not a floating banner */}
      <div className="flex items-start gap-3.5 border-b border-red-500/20 bg-red-500/[0.07] px-6 py-5 sm:px-8" role="note">
        <span
          aria-hidden="true"
          className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-red-500/40 bg-red-500/10"
        >
          <svg className="h-4 w-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.75}
              d="M12 9v3.75m0 3.5h.01M10.29 3.86 1.82 18a1.75 1.75 0 0 0 1.5 2.62h17.36a1.75 1.75 0 0 0 1.5-2.62L13.71 3.86a1.75 1.75 0 0 0-3.02 0Z"
            />
          </svg>
        </span>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-red-400">Danger zone</p>
          <p className="mt-1 text-sm leading-relaxed text-white/65">
            Submitting this form starts <strong className="font-semibold text-white">permanent deletion</strong> of
            your account. This cannot be undone or recovered once processed.
          </p>
        </div>
      </div>

      <div className="px-6 py-8 sm:px-8 sm:py-10">
        {/* 01 — Which app ------------------------------------------------- */}
        <fieldset className="m-0 border-0 p-0">
          <legend className="sr-only">Which app or service</legend>
          <StepHeader number="01" title="Which app or service" />

          {lockedApp ? (
            <div>
              <span className={labelBase}>App / Service</span>
              {/* App is fixed for app-specific deletion pages. The hidden
                  input is registered so the value still flows through the form. */}
              <input type="hidden" {...register('app')} defaultValue={lockedApp} />
              <div className="flex items-center gap-3 rounded-lg border border-white/[0.12] bg-black/25 px-4 py-3.5">
                <svg
                  aria-hidden="true"
                  className="h-4 w-4 flex-shrink-0 text-white/40"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.75}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-base text-white">{lockedApp}</span>
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor={fid('app')} className={labelBase}>
                App / Service <span className="text-red-400">*</span>
              </label>
              <select
                id={fid('app')}
                {...register('app')}
                aria-invalid={errors.app ? 'true' : 'false'}
                aria-describedby={errors.app ? fid('app-error') : undefined}
                className={cn(
                  fieldBase,
                  'cursor-pointer appearance-none',
                  errors.app ? fieldError : fieldIdle,
                  selectArrow
                )}
              >
                {apps.map((app) => (
                  <option
                    key={app.value}
                    value={app.value}
                    style={{ backgroundColor: '#0d211e', color: 'white' }}
                  >
                    {app.label}
                  </option>
                ))}
              </select>
              {errors.app && (
                <p id={fid('app-error')} role="alert" className="mt-2 text-sm text-red-400">
                  {errors.app.message}
                </p>
              )}
            </div>
          )}

          {!lockedApp && selectedApp === 'other' && (
            <div className="mt-6">
              <label htmlFor={fid('app-other')} className={labelBase}>
                Name of the App / Service <span className="text-red-400">*</span>
              </label>
              <input
                id={fid('app-other')}
                {...register('appOther')}
                type="text"
                placeholder="Which app or service is this about?"
                aria-invalid={errors.appOther ? 'true' : 'false'}
                aria-describedby={errors.appOther ? fid('app-other-error') : undefined}
                className={cn(fieldBase, errors.appOther ? fieldError : fieldIdle)}
              />
              {errors.appOther && (
                <p id={fid('app-other-error')} role="alert" className="mt-2 text-sm text-red-400">
                  {errors.appOther.message}
                </p>
              )}
            </div>
          )}
        </fieldset>

        {/* 02 — Identity --------------------------------------------------- */}
        <fieldset className="m-0 mt-12 border-0 p-0">
          <legend className="sr-only">Your account</legend>
          <StepHeader number="02" title="Your account" />

          <div className="grid gap-6 md:grid-cols-2">
            <div className="md:col-span-2">
              <label htmlFor={fid('email')} className={labelBase}>
                Email Address <span className="text-red-400">*</span>
              </label>
              <input
                id={fid('email')}
                {...register('email')}
                type="email"
                autoComplete="email"
                placeholder="Enter your registered email"
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? fid('email-error') : undefined}
                className={cn(fieldBase, errors.email ? fieldError : fieldIdle)}
              />
              {errors.email && (
                <p id={fid('email-error')} role="alert" className="mt-2 text-sm text-red-400">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor={fid('username')} className={labelBase}>
                Username / Account ID <span className="text-red-400">*</span>
              </label>
              <input
                id={fid('username')}
                {...register('username')}
                type="text"
                autoComplete="username"
                placeholder="Your username or account ID"
                aria-invalid={errors.username ? 'true' : 'false'}
                aria-describedby={errors.username ? fid('username-error') : undefined}
                className={cn(fieldBase, errors.username ? fieldError : fieldIdle)}
              />
              {errors.username && (
                <p id={fid('username-error')} role="alert" className="mt-2 text-sm text-red-400">
                  {errors.username.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor={fid('phone')} className={labelBase}>
                Phone Number <span className="normal-case text-white/30">(Optional)</span>
              </label>
              <input
                id={fid('phone')}
                {...register('phoneNumber')}
                type="tel"
                autoComplete="tel"
                placeholder="Your registered phone number"
                className={cn(fieldBase, fieldIdle)}
              />
            </div>
          </div>
        </fieldset>

        {/* 03 — Reason ----------------------------------------------------- */}
        <fieldset className="m-0 mt-12 border-0 p-0">
          <legend className="sr-only">Tell us why</legend>
          <StepHeader number="03" title="Tell us why" />

          <div>
            <label htmlFor={fid('reason')} className={labelBase}>
              Reason for Deletion <span className="text-red-400">*</span>
            </label>
            <select
              id={fid('reason')}
              {...register('reason')}
              aria-invalid={errors.reason ? 'true' : 'false'}
              aria-describedby={errors.reason ? fid('reason-error') : undefined}
              className={cn(
                fieldBase,
                'cursor-pointer appearance-none',
                errors.reason ? fieldError : fieldIdle,
                selectArrow
              )}
            >
              {deletionReasons.map((reason) => (
                <option
                  key={reason.value}
                  value={reason.value}
                  style={{ backgroundColor: '#0d211e', color: 'white' }}
                >
                  {reason.label}
                </option>
              ))}
            </select>
            {errors.reason && (
              <p id={fid('reason-error')} role="alert" className="mt-2 text-sm text-red-400">
                {errors.reason.message}
              </p>
            )}
          </div>

          <div className="mt-6">
            <label htmlFor={fid('additional')} className={labelBase}>
              Additional Information{' '}
              <span className={selectedReason === 'other' ? 'text-red-400' : 'normal-case text-white/30'}>
                {selectedReason === 'other' ? '*' : '(Optional)'}
              </span>
            </label>
            <textarea
              id={fid('additional')}
              {...register('additionalInfo')}
              rows={4}
              placeholder="Please provide any additional details that may help us process your request..."
              className={cn(fieldBase, fieldIdle, 'resize-none leading-relaxed')}
            />
          </div>
        </fieldset>

        {/* 04 — Final confirmation ----------------------------------------- */}
        <div className="mt-12">
          <StepHeader number="04" title="Final confirmation" />

          <div className="rounded-xl border border-red-500/25 bg-red-500/[0.05] p-5 sm:p-6">
            <h3 className="text-sm font-semibold text-white">What you will lose</h3>
            <p className="mt-1 text-sm text-white/50">
              Once your request is processed, the following is erased and cannot be restored:
            </p>
            <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
              {consequences.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm leading-snug text-white/70">
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            {/* Acknowledgement checkbox (drives the schema's confirmDeletion) */}
            <div className="mt-6 border-t border-red-500/15 pt-6">
              <label htmlFor={fid('confirm')} className="group flex cursor-pointer items-start gap-3.5">
                <span className="relative mt-0.5">
                  <input
                    id={fid('confirm')}
                    {...register('confirmDeletion')}
                    type="checkbox"
                    className="peer sr-only"
                    aria-describedby={errors.confirmDeletion ? fid('confirm-error') : undefined}
                  />
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-5 w-5 items-center justify-center rounded border-2 transition-colors',
                      errors.confirmDeletion ? 'border-red-400' : 'border-white/30 group-hover:border-white/60',
                      'peer-checked:border-red-500 peer-checked:bg-red-500',
                      'peer-focus-visible:ring-2 peer-focus-visible:ring-red-400 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[#0d211e]'
                    )}
                  >
                    <svg
                      className="h-3.5 w-3.5 text-white opacity-0 transition-opacity peer-checked:opacity-100"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3.5}
                    >
                      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </span>
                <span className="text-sm leading-relaxed text-white/75">
                  I understand that this action is{' '}
                  <strong className="font-semibold text-white">permanent and irreversible</strong>, and that my
                  account and all associated data will be deleted and cannot be recovered.
                </span>
              </label>
              {errors.confirmDeletion && (
                <p id={fid('confirm-error')} role="alert" className="mt-2.5 pl-9 text-sm text-red-400">
                  {errors.confirmDeletion.message}
                </p>
              )}
            </div>

            {/* Type-to-confirm gate */}
            <div className="mt-6 border-t border-red-500/15 pt-6">
              <label htmlFor={fid('confirm-word')} className="mb-2.5 block text-sm text-white/70">
                To confirm, type{' '}
                <span className="select-none font-semibold tracking-[0.15em] text-red-400">{CONFIRMATION_WORD}</span>{' '}
                below
              </label>
              <input
                id={fid('confirm-word')}
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                placeholder={CONFIRMATION_WORD}
                aria-describedby={fid('confirm-word-hint')}
                className={cn(
                  'w-full rounded-lg border bg-black/30 px-4 py-3 text-base tracking-[0.15em] text-white outline-none transition-colors placeholder:tracking-[0.15em] placeholder:text-white/25',
                  isConfirmed
                    ? 'border-red-500/70 focus-visible:border-red-400'
                    : 'border-white/20 focus-visible:border-white/50'
                )}
              />
              <p id={fid('confirm-word-hint')} className="mt-2 text-xs text-white/40">
                {isConfirmed
                  ? 'Confirmed — the deletion request is now unlocked.'
                  : `The button below stays locked until you type ${CONFIRMATION_WORD} exactly.`}
              </p>
            </div>
          </div>
        </div>

        {/* Submit ---------------------------------------------------------- */}
        <button
          type="submit"
          disabled={isSubmitting || !isConfirmed}
          className={cn(
            'mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-xl px-8 py-4 text-base font-semibold transition-all duration-300',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d211e]',
            isConfirmed && !isSubmitting
              ? 'cursor-pointer bg-red-600 text-white shadow-[0_8px_30px_-8px_rgba(220,38,38,0.55)] hover:bg-red-500'
              : 'cursor-not-allowed border border-white/15 bg-white/[0.04] text-white/35'
          )}
        >
          {!isSubmitting &&
            (isConfirmed ? (
              <svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            ) : (
              <svg aria-hidden="true" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                />
              </svg>
            ))}
          {isSubmitting ? 'Submitting Request…' : 'Delete My Account'}
        </button>

        {/* Status Messages */}
        <div aria-live="polite">
          {submitStatus === 'success' && (
            <div className="mt-6 rounded-xl border border-green-500/30 bg-green-500/10 p-5" role="status">
              <div className="flex items-start gap-3.5">
                <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="mb-1 text-sm font-semibold text-green-400">Request Submitted Successfully</h4>
                  <p className="text-sm leading-relaxed text-green-400/80">
                    Your account deletion request has been received. You will receive a confirmation email within 48
                    hours once your account has been deleted.
                  </p>
                </div>
              </div>
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 p-5" role="alert">
              <div className="flex items-start gap-3.5">
                <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="mb-1 text-sm font-semibold text-red-400">Submission Failed</h4>
                  <p className="text-sm leading-relaxed text-red-400/80">
                    Something went wrong. Please try again or contact us directly at{' '}
                    <a href="mailto:info@datacode.app" className="underline hover:text-red-300">
                      info@datacode.app
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
